import React, { Suspense, lazy, useEffect, useState, useCallback, useRef, createContext, useContext } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useAuth, AuthProvider } from "./utils/authContext";
import { useAuthListener } from "./utils/authStateListener";
import { checkUserPaymentStatus } from "./utils/paymentUtils";
import ProtectedRoute from "./utils/ProtectedRoute";
import Navbar from "./components/Navbar";
import JourneyNavigation from "./components/onboarding/JourneyNavigation";
import Footer from "./components/Footer";
import Loader2 from "./components/Loader2";
import AnalyticsTracker from "./components/AnalyticsTracker";
import ConsentManager from "./components/ConsentManager";
import MetaPixelTracker from "./components/MetaPixelTracker";
import SEO from "./components/SEO";
import { isRedesignPath } from "./redesign/redesignRoutes";
import { marketingMessages } from "./i18n/marketingMessages";


// Scrolls window/root to top on every navigation and disables browser auto-restore.
import { useLayoutEffect as _useLayoutEffect } from "react";
import { useLocation as _useLocation } from "react-router-dom";
const ScrollToTopOnMount = () => {
  const location = _useLocation();

  useEffect(() => {
    const previous = window.history?.scrollRestoration;
    if (window.history && "scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    return () => {
      if (window.history && "scrollRestoration" in window.history) {
        window.history.scrollRestoration = previous || "auto";
      }
    };
  }, []);

  _useLayoutEffect(() => {
    const hash = location.hash;
    let frame;
    let observer;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const scrollNow = () => {
      if (hash) {
        let target;
        try { target = document.getElementById(decodeURIComponent(hash.slice(1))); } catch (_) { return false; }
        if (!target) return false;
        target.scrollIntoView({ behavior: reduce ? "instant" : "smooth", block: "start" });
        return true;
      }
      const opts = { top: 0, left: 0, behavior: "instant" };
      window.scrollTo(opts);
      document.getElementById("root")?.scrollTo?.(opts);
      return true;
    };
    if (hash) {
      // Home establishes its pinned layout after mounting. Measure the anchor once
      // that layout has settled, including lazy-route entry, without two scrolls.
      const scheduleHashScroll = () => {
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(() => {
          frame = window.requestAnimationFrame(() => {
            if (scrollNow()) observer?.disconnect();
          });
        });
      };
      observer = new MutationObserver(scheduleHashScroll);
      observer.observe(document.getElementById("root"), { childList: true, subtree: true });
      scheduleHashScroll();
    } else {
      scrollNow();
      frame = window.requestAnimationFrame(scrollNow);
    }
    return () => { window.cancelAnimationFrame(frame); observer?.disconnect(); };
  }, [location.key, location.pathname, location.search, location.hash]);
  return null;
}

const SUPPORTED_LOCALES = ["en","fr","de","es","it","pt","el","zh","ja","ko","bn","ar","yi"];
const normalizeLocale = (value) => {
  if (!value) return null;
  const lower = String(value).toLowerCase();
  const base = lower.split("-")[0];
  if (SUPPORTED_LOCALES.includes(lower)) return lower;
  if (SUPPORTED_LOCALES.includes(base)) return base;
  return null;
};
const CommercialEnquiry = lazy(() => import("./pages/CommercialEnquiry"));
const ForShops = lazy(() => import("./pages/redesign/acquisition/ForShops"));
const Discover = lazy(() => import("./pages/redesign/acquisition/Discover"));
const HomeRedesign = lazy(() => import("./pages/redesign/HomeRedesign"));
const Solutions = lazy(() => import("./pages/Solutions"));
// Redesign 2026 pages (generated from approved prototypes). Legacy page modules stay in the repo for rollback.
const Product = lazy(() => import("./pages/redesign/product/ProductRedesign"));
const Pricing = lazy(() => import("./pages/redesign/pricing/PricingRedesign"));
const Resources = lazy(() => import("./pages/old articles"));
const Signup = lazy(() => import("./pages/Signup"));
const WhiteLabelSolutionsPage = lazy(() => import("./pages/whitelabel/WhiteLabelSolutionsPage"));
const WhiteLabelStandardSignup = lazy(() => import("./pages/whitelabel/WhiteLabelStandardSignup"));
const WhiteLabelMultiBrandSignup = lazy(() => import("./pages/whitelabel/WhiteLabelMultiBrandSignup"));
const WhiteLabelLuxurySignup = lazy(() => import("./pages/whitelabel/WhiteLabelLuxurySignup"));
const SetupComplete = lazy(() => import("./pages/SetupComplete"));
const Login = lazy(() => import("./pages/Login"));
const OnboardingSetup = lazy(() => import("./pages/OnboardingSetup"));
const Payment = lazy(() => import("./pages/Payment"));
const Success = lazy(() => import("./pages/Success"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const Receipt = lazy(() => import("./pages/Receipt"));
const BillingPortal = lazy(() => import("./pages/BillingPortal"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const OnboardingOverview = lazy(() => import("./pages/OnboardingOverview"));
const MyAccount = lazy(() => import("./pages/MyAccount"));
const AdminApprovePayment = lazy(() => import("./pages/AdminApprovePayment")); // Moved AdminApprovePayment to pages folder
const Register = lazy(() => import("./pages/Register"));
const FriendsAndFamilyPortal = lazy(() => import("./pages/FooterLinks/Partners/FriendsAndFamilyPortal"));

// New lazy imports for article pages
const ArticleContent1 = lazy(() => import("./components/resources/ArticleContent1"));
const ArticleContent2 = lazy(() => import("./components/resources/ArticleContent2"));
const ArticleContent3 = lazy(() => import("./components/resources/ArticleContent3"));

// FooterLinks pages (folder name corrected)
const TermsOfService = lazy(() => import("./pages/FooterLinks/TermsOfService"));
const GDPRCompliance = lazy(() => import("./pages/FooterLinks/GDPRCompliance"));
const PrivacyPolicy = lazy(() => import("./pages/FooterLinks/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/FooterLinks/CookiePolicy"));
const DeleteAccount = lazy(() => import("./pages/FooterLinks/DeleteAccount"));
const FAQPage = lazy(() => import("./pages/FooterLinks/FAQs"));
const Partners = lazy(() => import("./pages/FooterLinks/Partners/Partners"));
const InvestorPage = lazy(() => import("./pages/FooterLinks/Partners/investorPage"));
const MerchantPage = lazy(() => import("./pages/FooterLinks/Partners/merchantPage"));
const Roadmap = lazy(() => import("./pages/FooterLinks/Partners/Roadmap"));
const PlatformEvolutionPage = lazy(() => import("./pages/FooterLinks/Partners/PlatformEvolutionPage"));

// Added lazy import for FindYourFit page
const FindYourFit = lazy(() => import("./pages/redesign/fit/FitRedesign"));
const BusinessShowcase = lazy(() => import("./pages/BusinessShowcase"));
const WebSummit = lazy(() => import("./pages/WebSummit"));
const LoyaltyProgram = lazy(() => import("./pages/LoyaltyProgram"));
const ReferralLanding = lazy(() => import("./pages/ReferralLanding"));
const MentionMePartner = lazy(() => import("./pages/partners/MentionMePartner"));
const MENTIONME_PARTNER_ALIAS_PATH = "/anca-mentionme-20260216-9f3a2";
const Enterprise = lazy(() => import("./pages/redesign/enterprise/EnterpriseRedesign"));
const AboutPreviewPage = lazy(() => import("./pages/redesign/story/StoryRedesign"));
const SmartDownloadPage = lazy(() => import("./pages/SmartDownloadPage"));
const isSmartDownloadPath = (pathname = "") =>
  pathname === "/get" || pathname.startsWith("/get/");

// Added lazy import for NotFound page

const NotFound = lazy(() => import("./pages/NotFound"));
// --- New pages ---
const Partners2 = lazy(() => import("./pages/FooterLinks/Partners/Partners2"));
const PilotApply = lazy(() => import("./pages/FooterLinks/Partners/PilotApply"));
const PilotPage = lazy(() => import("./pages/FooterLinks/Partners/PilotPage"));
const PilotMOU = lazy(() => import("./pages/FooterLinks/Partners/PilotMOU"));
const PilotLOI = lazy(() => import("./pages/FooterLinks/Partners/PilotLOI"));
const PilotThanks = lazy(() => import("./pages/FooterLinks/Partners/PilotThanks"));

// --- i18n (minimal) ---
export const I18nContext = createContext({ locale: "en", setLocale: () => {}, t: (k) => k });
// Simple hook + component for translations across the app
export const useI18n = () => React.useContext(I18nContext);
export const T = ({ k, children }) => {
  const { t } = React.useContext(I18nContext);
  const out = t(k);
  return out ?? children ?? null;
};

const MESSAGES = {
  en: {
    "nav.home": "Home","nav.product": "Product","nav.findyourfit": "Find your fit","nav.businessShowcase": "Business showcase","nav.pricing": "Pricing","nav.solutions": "Solutions","nav.solutions.accessibility": "Accessibility","nav.solutions.smallBusiness": "Small Business","nav.solutions.community": "Community","auth.login": "Log in","auth.getStarted": "Get started","auth.myAccount": "Account Settings","auth.signOut": "Sign Out","auth.getStartedCaps": "Get Started",
    "home.hero.title": "Loyalty program software for independent retailers.",
    "home.hero.subtitle": "ZediPass replaces slow forms with instant QR sign-up, simple rewards, and real-time insights that drive repeat visits.",
    "home.hero.badge": "🚀 Early Access",
    "home.hero.headline": "Turn first-time visitors\ninto regulars",
    "home.hero.subheadline": "Customers join with one scan. You see who's coming back — and bring them back when they don't.",
    "home.cta.startFree": "Get Started",
    "home.cta.seeInAction": "See ZediPass in Action",
    "home.cta.findOutMore": "Find out more",
    "home.cta.getStarted": "Get Started",
    "home.cta.requestDemo": "Request Early Access Demo",
    ...(marketingMessages.en || {})
  },
  fr: {
    "nav.home": "Accueil","nav.product": "Produit","nav.findyourfit": "Trouvez votre formule","nav.businessShowcase": "Vitrine métiers","nav.pricing": "Tarifs","nav.solutions": "Solutions","nav.solutions.accessibility": "Accessibilité","nav.solutions.smallBusiness": "Petites entreprises","nav.solutions.community": "Communauté","auth.login": "Se connecter","auth.getStarted": "Commencer","auth.myAccount": "Paramètres du compte","auth.signOut": "Se déconnecter","auth.getStartedCaps": "Commencer",
    "home.hero.title": "Transformez plus de clients en membres. Instantanément.",
    "home.hero.subtitle": "ZediPass remplace les formulaires lents et les parcours d’inscription lourds par une fidélité sans friction, tap‑to‑join. Plus d’inscriptions manquées, plus de données perdues.",
    "home.hero.badge": "🚀 Early Access",
    "home.hero.headline": "La couche non intrusive qui rend l'inscription à la fidélité sans effort pour les entreprises de toute taille.",
    "home.hero.subheadline": "ZediPass donne aux petites entreprises les moyens de bénéficier des technologies des grandes marques à un coût abordable.",
    "home.cta.startFree": "Commencer",
    "home.cta.seeInAction": "Voir ZediPass en action",
    "home.cta.findOutMore": "En savoir plus",
    "home.cta.getStarted": "Get Started",
    "home.cta.requestDemo": "Demander une démo d'accès anticipé"
  },
  de: {
    "nav.home": "Startseite","nav.product": "Produkt","nav.findyourfit": "Finde dein Paket","nav.businessShowcase": "Branchenszenarien","nav.pricing": "Preise","nav.solutions": "Lösungen","nav.solutions.accessibility": "Barrierefreiheit","nav.solutions.smallBusiness": "Kleine Unternehmen","nav.solutions.community": "Community","auth.login": "Anmelden","auth.getStarted": "Loslegen","auth.myAccount": "Kontoeinstellungen","auth.signOut": "Abmelden","auth.getStartedCaps": "Loslegen",
    "home.hero.title": "Mehr Kunden zu Mitgliedern machen. Sofort.",
    "home.hero.subtitle": "ZediPass ersetzt langsame Formulare und umständliche Anmeldungen durch nahtlose Loyalty mit Tap‑to‑Join. Keine verpassten Anmeldungen, keine verlorenen Daten.",
    "home.hero.badge": "🚀 Early Access",
    "home.hero.headline": "Die nicht störende Ebene, die die Loyalitätsanmeldung für Unternehmen jeder Größe mühelos macht.",
    "home.hero.subheadline": "ZediPass befähigt kleine Unternehmen und bringt ihnen die Technologie großer Marken zu erschwinglichen Kosten.",
    "home.cta.startFree": "Kostenlos starten",
    "home.cta.seeInAction": "ZediPass in Aktion",
    "home.cta.findOutMore": "Mehr erfahren",
    "home.cta.getStarted": "Get Started",
    "home.cta.requestDemo": "Frühen Demo-Zugang anfordern"
  },
  es: {
    "nav.home": "Inicio","nav.product": "Producto","nav.findyourfit": "Encuentra tu plan","nav.businessShowcase": "Escenarios empresariales","nav.pricing": "Precios","nav.solutions": "Soluciones","nav.solutions.accessibility": "Accesibilidad","nav.solutions.smallBusiness": "Pequeñas empresas","nav.solutions.community": "Comunidad","auth.login": "Iniciar sesión","auth.getStarted": "Empezar","auth.myAccount": "Configuración de la cuenta","auth.signOut": "Cerrar sesión","auth.getStartedCaps": "Empezar",
    "home.hero.title": "Convierte más clientes en miembros. Al instante.",
    "home.hero.subtitle": "ZediPass reemplaza formularios lentos y registros torpes por fidelización sin fricción con tap‑to‑join. Sin registros perdidos ni datos extraviados.",
    "home.hero.badge": "🚀 Early Access",
    "home.hero.headline": "La capa no intrusiva que hace que la afiliación a programas de fidelidad sea sencilla para negocios de cualquier tamaño.",
    "home.hero.subheadline": "ZediPass potencia a las pequeñas empresas con la tecnología de las grandes marcas a un coste asequible.",
    "home.cta.startFree": "Empezar gratis",
    "home.cta.seeInAction": "Ver ZediPass en acción",
    "home.cta.findOutMore": "Saber más",
    "home.cta.getStarted": "Get Started",
    "home.cta.requestDemo": "Solicitar demo de acceso anticipado"
  },
  it: {
    "nav.home": "Home","nav.product": "Prodotto","nav.findyourfit": "Trova il tuo piano","nav.businessShowcase": "Vetrina business","nav.pricing": "Prezzi","nav.solutions": "Soluzioni","nav.solutions.accessibility": "Accessibilità","nav.solutions.smallBusiness": "Piccole imprese","nav.solutions.community": "Comunità","auth.login": "Accedi","auth.getStarted": "Inizia","auth.myAccount": "Impostazioni account","auth.signOut": "Esci","auth.getStartedCaps": "Inizia",
    "home.hero.title": "Trasforma più clienti in membri. Istantaneamente.",
    "home.hero.subtitle": "ZediPass sostituisce moduli lenti e iscrizioni macchinose con una loyalty senza attriti, tap‑to‑join. Niente più iscrizioni mancate né dati persi.",
    "home.hero.badge": "🚀 Early Access",
    "home.hero.headline": "Il livello non invasivo che rende l'iscrizione alla loyalty senza sforzo per aziende di ogni dimensione.",
    "home.hero.subheadline": "ZediPass dà potere alle piccole imprese offrendo la tecnologia dei grandi marchi a un costo accessibile.",
    "home.cta.startFree": "Inizia gratis",
    "home.cta.seeInAction": "Guarda ZediPass in azione",
    "home.cta.findOutMore": "Scopri di più",
    "home.cta.getStarted": "Get Started",
    "home.cta.requestDemo": "Richiedi la demo in anteprima"
  },
  pt: {
    "nav.home": "Início","nav.product": "Produto","nav.findyourfit": "Encontre o seu plano","nav.businessShowcase": "Vitrine de negócios","nav.pricing": "Preços","nav.solutions": "Soluções","nav.solutions.accessibility": "Acessibilidade","nav.solutions.smallBusiness": "Pequenas empresas","nav.solutions.community": "Comunidade","auth.login": "Entrar","auth.getStarted": "Começar","auth.myAccount": "Configurações da conta","auth.signOut": "Sair","auth.getStartedCaps": "Começar",
    "home.hero.title": "Transforme mais clientes em membros. Instantaneamente.",
    "home.hero.subtitle": "O ZediPass substitui formulários lentos e cadastros complicados por fidelidade sem atrito com tap‑to‑join. Sem inscrições perdidas nem dados extraviados.",
    "home.hero.badge": "🚀 Early Access",
    "home.hero.headline": "A camada não intrusiva que torna a adesão à fidelidade simples para negócios de qualquer porte.",
    "home.hero.subheadline": "O ZediPass capacita pequenos negócios com a tecnologia das grandes marcas a um custo acessível.",
    "home.cta.startFree": "Começar grátis",
    "home.cta.seeInAction": "Ver o ZediPass em ação",
    "home.cta.findOutMore": "Saiba mais",
    "home.cta.getStarted": "Get Started",
    "home.cta.requestDemo": "Solicitar demo de acesso antecipado"
  },
  el: {
    "nav.home": "Αρχική","nav.product": "Προϊόν","nav.findyourfit": "Βρες το πλάνο σου","nav.businessShowcase": "Επιχειρησιακά σενάρια","nav.pricing": "Τιμές","nav.solutions": "Λύσεις","nav.solutions.accessibility": "Προσβασιμότητα","nav.solutions.smallBusiness": "Μικρές επιχειρήσεις","nav.solutions.community": "Κοινότητα","auth.login": "Σύνδεση","auth.getStarted": "Ξεκινήστε","auth.myAccount": "Ρυθμίσεις λογαριασμού","auth.signOut": "Αποσύνδεση","auth.getStartedCaps": "Ξεκινήστε",
    "home.hero.title": "Μετατρέψτε περισσότερους πελάτες σε μέλη. Άμεσα.",
    "home.hero.subtitle": "Το ZediPass αντικαθιστά τις αργές φόρμες και τα δύσχρηστα sign‑up με απρόσκοπτη πιστότητα tap‑to‑join. Χωρίς χαμένες εγγραφές ή δεδομένα.",
    "home.hero.badge": "🚀 Early Access",
    "home.hero.headline": "Το διακριτικό επίπεδο που κάνει την εγγραφή σε προγράμματα πιστότητας χωρίς κόπο για επιχειρήσεις κάθε μεγέθους.",
    "home.hero.subheadline": "Το ZediPass ενδυναμώνει τις μικρές επιχειρήσεις με τεχνολογία μεγάλων brands σε προσιτό κόστος.",
    "home.cta.startFree": "Ξεκινήστε δωρεάν",
    "home.cta.seeInAction": "Δείτε το ZediPass στην πράξη",
    "home.cta.findOutMore": "Μάθετε περισσότερα",
    "home.cta.getStarted": "Get Started",
    "home.cta.requestDemo": "Ζητήστε demo πρόωρης πρόσβασης"
  },
  zh: {
    "nav.home": "首页","nav.product": "产品","nav.findyourfit": "选择适合你的方案","nav.businessShowcase": "业务场景","nav.pricing": "定价","nav.solutions": "解决方案","nav.solutions.accessibility": "无障碍","nav.solutions.smallBusiness": "小型企业","nav.solutions.community": "社区","auth.login": "登录","auth.getStarted": "开始使用","auth.myAccount": "账户设置","auth.signOut": "退出登录","auth.getStartedCaps": "开始使用",
    "home.hero.title": "把更多顾客转化为会员。即刻。",
    "home.hero.subtitle": "ZediPass 以无缝的一键加入替代缓慢表单和笨拙注册。不错失报名，不丢失数据。",
    "home.hero.badge": "🚀 Early Access",
    "home.hero.headline": "让各类规模的企业都能轻松完成忠诚度注册的无干扰层。",
    "home.hero.subheadline": "ZediPass 赋能小型企业，以可负担的成本享受大品牌级技术。",
    "home.cta.startFree": "免费开始",
    "home.cta.seeInAction": "了解 ZediPass 实际效果",
    "home.cta.findOutMore": "了解更多",
    "home.cta.getStarted": "Get Started",
    "home.cta.requestDemo": "申请早期体验演示",
    ...(marketingMessages.zh || {})
  },
  ja: {
    "nav.home": "ホーム","nav.product": "製品","nav.findyourfit": "最適なプランを探す","nav.businessShowcase": "ビジネス活用例","nav.pricing": "料金","nav.solutions": "ソリューション","nav.solutions.accessibility": "アクセシビリティ","nav.solutions.smallBusiness": "中小企業","nav.solutions.community": "コミュニティ","auth.login": "ログイン","auth.getStarted": "はじめる","auth.myAccount": "アカウント設定","auth.signOut": "ログアウト","auth.getStartedCaps": "はじめる",
    "home.hero.title": "より多くの顧客を会員に。すぐに。",
    "home.hero.subtitle": "ZediPass は遅いフォームや煩雑な登録を、タップで参加できるシームレスなロイヤルティに置き換えます。登録漏れやデータ消失を防ぎます。",
    "home.hero.badge": "🚀 Early Access",
    "home.hero.headline": "あらゆる規模のビジネスのロイヤルティ登録を手間なくする、邪魔にならないレイヤー。",
    "home.hero.subheadline": "ZediPass は、小規模ビジネスが大手ブランドのテクノロジーを手頃なコストで活用できるよう支援します。",
    "home.cta.startFree": "無料で始める",
    "home.cta.seeInAction": "ZediPass を見る",
    "home.cta.findOutMore": "さらに詳しく",
    "home.cta.getStarted": "Get Started",
    "home.cta.requestDemo": "早期アクセスデモをリクエスト"
  },
  ko: {
    "nav.home": "홈","nav.product": "제품","nav.findyourfit": "내게 맞는 요금제","nav.businessShowcase": "비즈니스 사례","nav.pricing": "가격","nav.solutions": "솔루션","nav.solutions.accessibility": "접근성","nav.solutions.smallBusiness": "소상공인","nav.solutions.community": "커뮤니티","auth.login": "로그인","auth.getStarted": "시작하기","auth.myAccount": "계정 설정","auth.signOut": "로그아웃","auth.getStartedCaps": "시작하기",
    "home.hero.title": "더 많은 고객을 즉시 멤버로 전환.",
    "home.hero.subtitle": "ZediPass는 느린 양식과 번거로운 가입을 탭 한 번의 무마찰 로열티로 바꿉니다. 놓치는 가입과 유실 데이터가 없습니다.",
    "home.hero.badge": "🚀 Early Access",
    "home.hero.headline": "규모와 상관없이 비즈니스의 로열티 가입을 손쉽게 만들어 주는 방해 없는 레이어.",
    "home.hero.subheadline": "ZediPass는 소상공인이 합리적인 비용으로 대기업 수준의 기술을 활용하도록 돕습니다.",
    "home.cta.startFree": "무료로 시작",
    "home.cta.seeInAction": "ZediPass 살펴보기",
    "home.cta.findOutMore": "자세히 보기",
    "home.cta.getStarted": "Get Started",
    "home.cta.requestDemo": "얼리 액세스 데모 요청"
  },
  bn: {
    "nav.home": "হোম","nav.product": "পণ্য","nav.findyourfit": "আপনার প্ল্যান খুঁজুন","nav.businessShowcase": "ব্যবসায়িক উদাহরণ","nav.pricing": "দাম","nav.solutions": "সমাধান","nav.solutions.accessibility": "প্রবেশগম্যতা","nav.solutions.smallBusiness": "ক্ষুদ্র ব্যবসা","nav.solutions.community": "কমিউনিটি","auth.login": "লগ ইন","auth.getStarted": "শুরু করুন","auth.myAccount": "অ্যাকাউন্ট সেটিংস","auth.signOut": "সাইন আউট","auth.getStartedCaps": "শুরু করুন",
    "home.hero.title": "আরও বেশি ক্রেতাকে সদস্যে পরিণত করুন—তাৎক্ষণিকভাবে।",
    "home.hero.subtitle": "ZediPass ধীর ফর্ম ও জটিল সাইন‑আপের বদলে ট্যাপ‑টু‑জয়েন নিরবচ্ছিন্ন লয়্যালটি দেয়। মিসড সাইন‑আপ বা ডেটা লস নয়।",
    "home.hero.badge": "🚀 Early Access",
    "home.hero.headline": "যে অদৃশ্য স্তরটি যেকোনো আকারের ব্যবসার জন্য লয়্যালটি সদস্যপদকে অনায়াস করে তোলে।",
    "home.hero.subheadline": "ZediPass ছোট ব্যবসাকে ক্ষমতায়িত করে, সাশ্রয়ী খরচে বড় ব্র্যান্ডের প্রযুক্তি তাদের হাতে তুলে দেয়।",
    "home.cta.startFree": "ফ্রি শুরু করুন",
    "home.cta.seeInAction": "ZediPass দেখুন",
    "home.cta.findOutMore": "আরও জানুন",
    "home.cta.getStarted": "Get Started",
    "home.cta.requestDemo": "আগাম প্রবেশ ডেমো অনুরোধ করুন"
  },
  ar: {
    "nav.home": "الصفحة الرئيسية","nav.product": "المنتج","nav.findyourfit": "اختر الخطة المناسبة","nav.businessShowcase": "استخدامات الأعمال","nav.pricing": "الأسعار","nav.solutions": "الحلول","nav.solutions.accessibility": "إمكانية الوصول","nav.solutions.smallBusiness": "الأعمال الصغيرة","nav.solutions.community": "المجتمع","auth.login": "تسجيل الدخول","auth.getStarted": "ابدأ الآن","auth.myAccount": "إعدادات الحساب","auth.signOut": "تسجيل الخروج","auth.getStartedCaps": "ابدأ الآن",
    "home.hero.title": "حوِّل المزيد من العملاء إلى أعضاء. فورًا.",
    "home.hero.subtitle": "يستبدل ZediPass النماذج البطيئة وتدفّقات التسجيل المرهقة بولاء سلس بتفعيل بلمسة. لا تسجيلات فائتة ولا بيانات مفقودة.",
    "home.hero.badge": "🚀 Early Access",
    "home.hero.headline": "الطبقة غير المزعجة التي تجعل الانضمام إلى برامج الولاء سهلاً لأي نشاط تجاري مهما كان حجمه.",
    "home.hero.subheadline": "يمنح ZediPass الشركات الصغيرة قوة تقنيات العلامات الكبرى بتكلفة معقولة.",
    "home.cta.startFree": "ابدأ مجانًا",
    "home.cta.seeInAction": "شاهد ZediPass عمليًا",
    "home.cta.findOutMore": "اعرف المزيد",
    "home.cta.getStarted": "Get Started",
    "home.cta.requestDemo": "اطلب عرضاً توضيحياً للوصول المبكر"
  },
  yi: {
    "nav.home": "היים","nav.product": "פּראָדוקט","nav.findyourfit": "געפין דיין פּלאן","nav.businessShowcase": "באַשעפֿט טאשונגען","nav.pricing": "פּרייזן","nav.solutions": "לייזונגען","nav.solutions.accessibility": "צוטריטלעכקייט","nav.solutions.smallBusiness": "קליינע פֿירמעס","nav.solutions.community": "קאָמוניטי","auth.login": "אַרײַנלאָגירן","auth.getStarted": "אָנהייבן","auth.myAccount": "אַקאַונט־אײַנשטעלונגען","auth.signOut": "אַרויסגיין","auth.getStartedCaps": "אָנהייבן",
    "home.hero.title": "מאך מער קאַסטאַמערס צו מיטגלידער. גלײַך איצט.",
    "home.hero.subtitle": "ZediPass פאַרבייַט די פּאַמעלעכע פארמען און אומבאַקוועמע אָנמלדונגען מיט אַן אָן־פֿריקציע טאַפּ־טו־דזשױן לױאַליטי. קיין פאַרפאַלענע אָנמלדונגען און קיין פֿאַרלוירענע דאַטן.",
    "home.hero.badge": "🚀 Early Access",
    "home.hero.headline": "די ניט-באַדערנדיקע שיכטע וואָס מאכט לויאַליטעט-איינשרייבן אומבאַשווערן פֿאַר געשעפטן פון אַלע גרעסן.",
    "home.hero.subheadline": "ZediPass געבט קליינע ביזנעסן די כוחות פון גרויסע ברענד-טעקנאָלאָגיע פאר אַ באצאָלבאַרן פּרײַז.",
    "home.cta.startFree": "הייב אָן בחינם",
    "home.cta.seeInAction": "זע ZediPass אין אַקציע",
    "home.cta.findOutMore": "דערפֿאַרן מער",
    "home.cta.getStarted": "Get Started",
    "home.cta.requestDemo": "בעטן אַ פרי-אקסעס דעמאָ"
  }
};

const useLocaleManager = () => {
  const [locale, setLocaleState] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const q = params.get("lang");
      const fromQuery = normalizeLocale(q);
      if (fromQuery) {
        localStorage.setItem("zedi_locale", fromQuery);
        return fromQuery;
      }
      const saved = normalizeLocale(localStorage.getItem("zedi_locale"));
      if (saved) return saved;
      return "en";
    } catch {
      return "en";
    }
  });

  const persistLocale = useCallback(
    (next) => {
      const chosen = normalizeLocale(next) || "en";
      setLocaleState((prev) => (prev === chosen ? prev : chosen));
      try {
        localStorage.setItem("zedi_locale", chosen);
      } catch {}
      try {
        const url = new URL(window.location.href);
        url.searchParams.set("lang", chosen);
        window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
      } catch {}
    },
    []
  );

  useEffect(() => {
    const h = (e) => {
      if (e?.detail?.locale) {
        persistLocale(e.detail.locale);
      }
    };
    window.addEventListener("zedipass:language-change", h);
    return () => window.removeEventListener("zedipass:language-change", h);
  }, [persistLocale]);

  useEffect(() => {
    try {
      document.documentElement.setAttribute("lang", locale);
      document.documentElement.setAttribute(
        "dir",
        ["ar", "yi"].includes(locale) ? "rtl" : "ltr"
      );
    } catch {}
  }, [locale]);

  const t = useCallback(
    (k) => MESSAGES[locale]?.[k] ?? MESSAGES.en?.[k] ?? k,
    [locale]
  );

  return { locale, setLocale: persistLocale, t };
};



const AppRoutes = () => {
  const { setLocale } = useContext(I18nContext);
  const { user } = useAuth();
  useAuthListener(); // ✅ Ensures authentication state is tracked

  const location = useLocation();

  const paymentCheckEnabled = (() => {
    const path = (location?.pathname || "").toLowerCase();
    const allowed = ["/myaccount", "/billing", "/receipt", "/payment", "/success", "/onboardingoverview"];
    return allowed.some((prefix) => path.startsWith(prefix));
  })();

  // 🛡️ Guarded payment status checker (prevents spam under StrictMode)
  const paymentCheckInFlight = useRef(false);
  const lastPaymentCheckAt = useRef(0);

  const checkPaymentStatusOnce = useCallback(async (opts = {}) => {
    if (!user) return;
    const forceRemote = Boolean(opts.forceRemote);
    if (paymentCheckInFlight.current) return;

    const now = Date.now();
    // Throttle to once per 60s unless explicitly forced
    if (!forceRemote && now - lastPaymentCheckAt.current < 60_000) return;

    paymentCheckInFlight.current = true;
    console.log("⏳ Checking payment status...");
    try {
      const status = await checkUserPaymentStatus(user.uid, { forceRemote });
      console.log("💳 Payment status resolved:", status);
    } catch (error) {
      console.error("❌ Error checking payment status:", error);
    } finally {
      lastPaymentCheckAt.current = Date.now();
      paymentCheckInFlight.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  // ✅ Prevent unnecessary updates if user is null
  useEffect(() => {
    if (!user) {
      return;
    }
    if (!paymentCheckEnabled) {
      return;
    }
    // Fire a single check when the user changes
    checkPaymentStatusOnce({ forceRemote: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, paymentCheckEnabled]);

  // 🔄 Gentle background polling + refresh on focus/visibility
  useEffect(() => {
    if (!user || !paymentCheckEnabled) return;
    const onFocus = () => checkPaymentStatusOnce({ forceRemote: false });
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkPaymentStatusOnce({ forceRemote: false });
      }
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener('visibilitychange', onVisibility);
    const id = setInterval(() => checkPaymentStatusOnce({ forceRemote: false }), 5 * 60 * 1000);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('visibilitychange', onVisibility);
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, checkPaymentStatusOnce, paymentCheckEnabled]);


  // 🔤 Sync locale from URL query (?lang=xx) on route changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get("lang");
    if (!q) return;
    const lower = String(q).toLowerCase();
    const base = lower.split("-")[0];
    const chosen = SUPPORTED_LOCALES.includes(lower)
      ? lower
      : SUPPORTED_LOCALES.includes(base)
      ? base
      : null;
    if (chosen) {
      setLocale(chosen);
    }
  }, [location.search, setLocale]);

  // Hide site chrome for private partner pages (/p/*)
  const isPrivatePartnerPage =
    (location.pathname || "").startsWith("/p/") ||
    (location.pathname || "") === "/p" ||
    (location.pathname || "") === MENTIONME_PARTNER_ALIAS_PATH;
  const isSmartDownloadPage = isSmartDownloadPath(location.pathname || "");
  const isAuthJourneyPage = ["/signup", "/login", "/forgot-password", "/verify-email", "/setup-complete", "/success", "/myaccount", "/onboardingoverview", "/onboardingsetup", "/business-setup"].includes(location.pathname) && !new URLSearchParams(location.search).get("flow");
  const isStandalonePage = isPrivatePartnerPage || isSmartDownloadPage || isAuthJourneyPage;
  // Redesigned pages bring their own nav and footer (RedesignShell).
  const isRedesignPage = isRedesignPath(location.pathname || "/");
  const showLegacyChrome = !isStandalonePage && !isRedesignPage;

  return (
    <div className="flex flex-col min-h-screen">
      {showLegacyChrome && <Navbar />}
      {isAuthJourneyPage && !["/signup", "/login", "/forgot-password", "/myaccount"].includes(location.pathname) && <JourneyNavigation />}
      <main className={`w-full flex-grow ${showLegacyChrome ? "pt-16 bg-gray-50 dark:bg-gray-900" : ""}`}>
        {!isSmartDownloadPage && <ConsentManager />}
        <ScrollToTopOnMount />
        <AnalyticsTracker />
        <MetaPixelTracker />
        {!isPrivatePartnerPage && <SEO />}
        <Suspense fallback={<Loader2 text="💭Loading..." />}>
          <Routes>
            {/* Specific partner route (kept for backwards compatibility) */}
            <Route path={MENTIONME_PARTNER_ALIAS_PATH} element={<MentionMePartner />} />
            <Route path="/p/anca-mentionme-20260216-9f3a2" element={<MentionMePartner />} />
            {/* Generic private partner routes (handles trailing slashes and future private links) */}
            <Route path="/p/:slug" element={<MentionMePartner />} />
            <Route path="/p/:slug/*" element={<MentionMePartner />} />

            {/* 🏠 Public Routes (Always Accessible) */}
            <Route path="/" element={<HomeRedesign />} />
            <Route path="/home" element={<HomeRedesign />} />
            <Route path="/product" element={<Product />} />
            <Route path="/product/" element={<Product />} />
            <Route path="/solutions" element={<Solutions />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/resources" element={<Resources />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/signup/whitelabel" element={<WhiteLabelSolutionsPage />} />
            <Route path="/signup/whitelabel/standard" element={<WhiteLabelStandardSignup />} />
            <Route path="/signup/whitelabel/multibrand" element={<WhiteLabelMultiBrandSignup />} />
            <Route path="/signup/whitelabel/luxury" element={<WhiteLabelLuxurySignup />} />
            <Route path="/websummit" element={<WebSummit />} />
            <Route path="/register" element={<Register />} />
            <Route path="/friends-family" element={<FriendsAndFamilyPortal />} />
            <Route path="/friends-family/customer" element={<FriendsAndFamilyPortal />} />
            <Route path="/friends-family/retailer" element={<FriendsAndFamilyPortal />} />
            <Route path="/friends-family/fullsignup" element={<FriendsAndFamilyPortal />} />
            <Route path="/loyalty-program" element={<LoyaltyProgram />} />
            <Route path="/referral" element={<ReferralLanding />} />
            <Route path="/contact" element={<CommercialEnquiry />} />
            <Route path="/for-shops" element={<ForShops />} />
            <Route path="/discover" element={<Discover />} />
            <Route path="/enterprise" element={<Enterprise />} />
            <Route path="/white-label-referral" element={<Navigate to="/enterprise" replace />} />
            <Route path="/ref/:shareId" element={<ReferralLanding />} />
            <Route path="/our-story" element={<AboutPreviewPage />} />
            <Route path="/my-story" element={<Navigate to="/our-story" replace />} />
            <Route path="/about" element={<Navigate to="/our-story" replace />} />
            <Route path="/about-preview" element={<Navigate to="/our-story" replace />} />
            <Route path="/get" element={<SmartDownloadPage audience="customer" />} />
            <Route path="/get/customer" element={<SmartDownloadPage audience="customer" />} />
            <Route path="/get/business" element={<SmartDownloadPage audience="business" />} />
            
            {/* New Pilot + Partners2 routes */}
            <Route path="/partners2" element={<Partners2 />} />
            <Route path="/pilot" element={<PilotPage />} />
            <Route path="/pilot/mou" element={<PilotMOU />} />
            <Route path="/pilot/loi" element={<PilotLOI />} />
            <Route path="/pilot/apply" element={<PilotApply />} />
            <Route path="/partners/pilot-apply" element={<PilotApply />} />
            <Route path="/pilot/thanks" element={<PilotThanks />} />
            
            <Route path="/login" element={<Login />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />

            {/* New Article Routes */}
            <Route path="/resources/article-content-1" element={<ArticleContent1 />} />
            <Route path="/resources/article-content-2" element={<ArticleContent2 />} />
            <Route path="/resources/article-content-3" element={<ArticleContent3 />} />

            {/* FooterLinks Pages */}
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/gdpr" element={<GDPRCompliance />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="/delete-account" element={<DeleteAccount />} />

            {/* FAQ Page */}
            <Route path="/faq" element={<FAQPage />} />

            {/* Partners Page */}
            <Route path="/partners" element={<Partners />} />
            <Route path="/roadmap" element={<Roadmap />} />
            <Route path="/partners/investors" element={<InvestorPage />} />
            <Route path="/partners/investors/platform-evolution" element={<PlatformEvolutionPage />} />
            <Route path="/partners/merchant" element={<MerchantPage />} />
            
            {/* Redirect old paths to new nested partners URLs */}
            <Route path="/investors" element={<Navigate to="/partners/investors" replace />} />
            <Route path="/merchant" element={<Navigate to="/partners/merchant" replace />} />

            {/* Added FindYourFit Route */}
            <Route path="/findyourfit" element={<FindYourFit />} />
            <Route path="/business-showcase" element={<BusinessShowcase />} />

            {/* 🔒 Protected Routes (auth required, payment not required at route level) */}
            <Route element={<ProtectedRoute requiredPayment={false} />}>
              <Route path="/onboardingoverview" element={<OnboardingOverview />} />
              <Route path="/onboardingOverview" element={<Navigate to="/onboardingoverview" replace />} />
              <Route path="/onboardingsetup" element={<OnboardingSetup />} />
              <Route path="/business-setup" element={<OnboardingSetup />} />
              <Route path="/setup-complete" element={<SetupComplete />} />
              <Route path="/payment" element={<Payment />} />
              <Route path="/success" element={<Success />} />
              <Route path="/myaccount" element={<MyAccount />} />
              <Route path="/MyAccount" element={<Navigate to="/myaccount" replace />} />
              <Route path="/myAccount" element={<Navigate to="/myaccount" replace />} />
              <Route path="/admin/approve-payment" element={<AdminApprovePayment />} />
            </Route>

            <Route element={<ProtectedRoute requiredPayment={true} />}>
              <Route path="/receipt" element={<Receipt />} />
              <Route path="/billing" element={<BillingPortal />} />
            </Route>

            {/* ❓ Catch-All Route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      {showLegacyChrome && <Footer />}
      <ToastContainer />
    </div>
  );
};

const App = () => {
  const i18n = useLocaleManager();

  // Private partner pages should be ultra-light: no auth listeners, no payment checks,
  // no dashboard chrome, and ideally no Firestore listeners.
  const bootPath = (typeof window !== "undefined" ? window.location?.pathname : "") || "";
  const isPrivatePartnerBoot =
    bootPath === "/p" ||
    bootPath.startsWith("/p/") ||
    bootPath === MENTIONME_PARTNER_ALIAS_PATH;
  const isSmartDownloadBoot = isSmartDownloadPath(bootPath);

  if (isPrivatePartnerBoot) {
    return (
      <I18nContext.Provider value={i18n}>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Suspense fallback={<Loader2 text="💭Loading..." />}>
            <Routes>
              <Route path={MENTIONME_PARTNER_ALIAS_PATH} element={<MentionMePartner />} />
              <Route path="/p/:slug" element={<MentionMePartner />} />
              <Route path="/p/:slug/*" element={<MentionMePartner />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </Router>
      </I18nContext.Provider>
    );
  }

  if (isSmartDownloadBoot) {
    return (
      <I18nContext.Provider value={i18n}>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <SEO />
          <Suspense fallback={<Loader2 text="Opening the right app store…" />}>
            <Routes>
              <Route path="/get" element={<SmartDownloadPage audience="customer" />} />
              <Route path="/get/customer" element={<SmartDownloadPage audience="customer" />} />
              <Route path="/get/business" element={<SmartDownloadPage audience="business" />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </Router>
      </I18nContext.Provider>
    );
  }

  return (
    <I18nContext.Provider value={i18n}>
      <AuthProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <AppRoutes />
        </Router>
      </AuthProvider>
    </I18nContext.Provider>
  );
}

export default App;
